import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { CATEGORY_SLUGS } from "../lib/categories";
import MasonryFeed from "../components/MasonryFeed";

/**
 * /category/:slug — masonry of all photos in a single genre.
 *
 * Unknown slug → shows the "no photos" empty state with a link back to the
 * genre index (we don't 404 because the slug list might be updated later).
 */
export default function CategoryDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  const valid = CATEGORY_SLUGS.has(slug);

  useEffect(() => {
    if (!valid) {
      setPhotos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(`/photos?category=${slug}&limit=60`)
      .then(({ data }) => setPhotos(data))
      .finally(() => setLoading(false));
  }, [slug, valid]);

  const label = valid ? t(`cat_${slug}`) : slug;

  return (
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-12" data-testid="category-detail-page">
      <Link
        to="/categories"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-zinc-200 transition-colors"
        data-testid="category-back-link"
      >
        <ChevronLeft size={12} />
        {t("all_genres")}
      </Link>

      <div className="mt-6 mb-12 border-b border-zinc-900 pb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">
          {t("genres")}
        </div>
        <h1
          className="mt-3 font-serif font-light text-5xl sm:text-6xl text-zinc-50 tracking-tight leading-[1.05]"
          data-testid="category-detail-title"
        >
          {label}
        </h1>
        {valid && (
          <p
            className="mt-4 max-w-2xl text-zinc-400 font-light leading-relaxed"
            data-testid="category-detail-description"
          >
            {t(`cat_${slug}_desc`)}
          </p>
        )}
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.3em]">
          {t("loading") || "Loading…"}
        </div>
      ) : photos.length === 0 ? (
        <div
          className="text-zinc-500 font-light text-sm"
          data-testid="category-empty-state"
        >
          {t("no_photos_in_category")}
        </div>
      ) : (
        <MasonryFeed photos={photos} />
      )}
    </div>
  );
}
